import type { Session } from '@classgo/shared';
import { redirect } from '@tanstack/react-router';

import { ApiError } from '../api/client';
import { sessionQuery } from '../api/session-query';
import type { LoginSearch, RouterContext } from './types';

/**
 * Validates the `/login` search params: only `redirect` (an app path) is kept.
 *
 * @param {Record<string, unknown>} search -- The raw search params.
 * @returns The typed params.
 */
export const loginSearch = (search: Record<string, unknown>): LoginSearch => ({
  redirect: typeof search['redirect'] === 'string' ? search['redirect'] : undefined,
});

/**
 * `beforeLoad` of the signed-in layout: waits for the boot refresh and sends an anonymous
 * visitor to login with the way back.
 *
 * @param {RouterContext} context -- The router context.
 * @param {string} href -- Where the person was going.
 */
export const requireSession = async (context: RouterContext, href: string): Promise<void> => {
  await context.auth.ready();
  if (context.auth.getSnapshot().status !== 'authenticated') {
    throw redirect({ to: '/login', search: { redirect: href } });
  }
};

/**
 * Loader of the signed-in layout: `GET /me` before the first screen renders. A token refused
 * during this very first load is handled here, because no component has mounted yet.
 *
 * @param {RouterContext} context -- The router context.
 * @param {string} href -- Where the person was going.
 * @returns The session.
 */
export const loadSession = async (context: RouterContext, href: string) => {
  try {
    return await context.queryClient.ensureQueryData(sessionQuery(context.api));
  } catch (error) {
    if (error instanceof ApiError && error.code === 'UNAUTHORIZED') {
      throw redirect({ to: '/login', search: { redirect: href } });
    }
    throw error;
  }
};

/**
 * Sends a person whose role requires TOTP, and who has not enrolled, to the enrollment screen
 * (ticket #54); the API refuses everything else for them anyway. Call it right after
 * `loadSession` in the signed-in layout's loader.
 *
 * @param {Session} session -- The loaded session.
 * @param {string} href -- Where the person was going, restored after enrollment.
 */
export const requireMfa = (session: Session, href: string): void => {
  if (session.mfa.required && !session.mfa.enrolled) {
    throw redirect({ to: '/mfa', search: { redirect: href } });
  }
};
